import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/api';

/**
 * Minimal admin view of the Monitoring Disclosure & Acknowledgment state: the current
 * notice version, the disclosed capabilities, and per-employee acknowledgment status.
 * Read-only; the notice itself is shown to employees by the Companion.
 */
const statusColor = (status: string): 'success' | 'warning' | 'default' => {
  if (status === 'CURRENT') return 'success';
  if (status === 'ACKNOWLEDGMENT_REQUIRED') return 'warning';
  return 'default';
};

const MonitoringDisclosureCard: React.FC = () => {
  const { data: policyData } = useQuery({
    queryKey: ['monitoring', 'monitoring-policy'],
    queryFn: () => apiClient.monitoring.getMonitoringPolicy(),
  });
  const { data: acksData } = useQuery({
    queryKey: ['monitoring', 'acknowledgments'],
    queryFn: () => apiClient.monitoring.getMonitoringAcknowledgments(),
  });

  const policy = (policyData as any)?.data?.data;
  const acks = (acksData as any)?.data?.data;
  const enabled = (policy?.disclosed_capabilities || []).filter((c: any) => c.enabled);
  const employees = acks?.employees || [];

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>Monitoring Disclosure & Acknowledgment</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Employees see a monitoring notice in the TEKS TIME Companion and acknowledge it.
          This records who was informed of what, and when. It does not affect monitoring or
          attendance.
        </Typography>

        {policy && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="body2">
              Current notice version: <strong>{policy.notice_version}</strong>
              {policy.material_change_pending && (
                <Chip size="small" color="warning" label="Material change pending" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Disclosed: {enabled.length
                ? enabled.map((c: any) => c.label).join(', ')
                : 'None'}
            </Typography>
          </Box>
        )}

        {employees.length === 0 ? (
          <Alert severity="info">No acknowledgments recorded yet.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Acked Version</TableCell>
                <TableCell>Acknowledged At</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.map((e: any) => (
                <TableRow key={e.user_id}>
                  <TableCell>{e.employee_name || e.email || e.user_id}</TableCell>
                  <TableCell>{e.device || '—'}</TableCell>
                  <TableCell>{e.acknowledged_version}</TableCell>
                  <TableCell>{e.acknowledged_at ? new Date(e.acknowledged_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={statusColor(e.status)} label={e.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default MonitoringDisclosureCard;
